import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useField } from 'formik';
import { useInfiniteQuery } from '@tanstack/react-query';
import fontApiService from '@/dashboard/services/fonts/fontApiService';
import FontOption from './FontOption';
import FontComboboxCustom from './FontComboboxCustom';
import { useFontPreloader } from '@dashboard/hooks/useFontLoader';

const propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  tooltip: PropTypes.string,
  showLabel: PropTypes.bool,
};

const defaultProps = {
  placeholder: 'Search fonts...',
  showLabel: true,
};

// Virtualization configuration
const ITEM_HEIGHT = 28; // Height of each font option in pixels
const CONTAINER_HEIGHT = 240; // max-h-60 = 240px
const VISIBLE_ITEMS = Math.ceil(CONTAINER_HEIGHT / ITEM_HEIGHT);
const BUFFER_SIZE = 3; // Extra items to render above/below visible area

const FontCombobox = ({
  name,
  label,
  placeholder,
  tooltip,
  showLabel,
  value,
  onChange,
  hasError,
  defaultLabel,
}) => {
  const [query, setQuery] = useState('');
  const [scrollTop, setScrollTop] = useState(0);
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedFontData, setSelectedFontData] = useState(null);
  const scrollElementRef = useRef(null);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  // Create query key for fonts
  const fontsQueryKey = useMemo(() => {
    const key = ['fonts'];
    if (debouncedQuery) key.push('search', debouncedQuery);
    return key;
  }, [debouncedQuery]);

  // Fetch fonts using infinite query for pagination
  const {
    data,
    isLoading: loading,
    error: queryError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: fontsQueryKey,
    queryFn: ({ pageParam = 0 }) => {
      const offset = pageParam * 20;
      return fontApiService.getFonts({
        search: debouncedQuery,
        limit: 20,
        offset,
        useCache: false, // Let TanStack Query handle caching
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      // If we got less than 20 fonts, we've reached the end
      if (!lastPage || lastPage.length < 20) return undefined;
      return allPages.length; // Next page number
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    enabled: true,
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Flatten all pages into a single fonts array
  const fonts = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flat();
  }, [data]);

  // Fetch the selected font if it's not in the loaded pages
  useEffect(() => {
    if (!value || value === '' || value === 'default') {
      setSelectedFontData(null);
      return;
    }

    // Check if the selected font is already in the loaded fonts
    const fontExists = fonts.some(font => font.family === value);

    if (!fontExists && value !== selectedFontData?.family) {
      // Fetch the specific font
      fontApiService
        .getFontByFamily(value)
        .then(font => {
          if (font) {
            setSelectedFontData(font);
          }
        })
        .catch(error => {
          console.error('Error fetching selected font:', error);
        });
    } else if (fontExists && selectedFontData) {
      // Clear the selected font data if it's now in the loaded fonts
      setSelectedFontData(null);
    }
  }, [value, fonts, selectedFontData]);

  const error = queryError?.message || null;

  const defaultOptionLabel = defaultLabel || 'Auto';
  const defaultOption = useMemo(
    () => ({ _id: '', name: defaultOptionLabel }),
    [defaultOptionLabel],
  );

  // Transform API font data to the format expected by the component
  const fontOptions = useMemo(() => {
    const options = [defaultOption];

    // Add the selected font if it's not in the loaded fonts
    if (selectedFontData && !fonts.some(font => font.family === selectedFontData.family)) {
      options.push({
        _id: selectedFontData.family,
        name: selectedFontData.family,
        category: selectedFontData.category,
        weights: selectedFontData.weights,
      });
    }

    // Add all loaded fonts
    if (fonts && fonts.length > 0) {
      const googleFonts = fonts.map(font => ({
        _id: font.family || font, // Handle both API format and fallback
        name: font.family || font,
        category: font.category,
        weights: font.weights,
      }));
      options.push(...googleFonts);
    }

    return options;
  }, [fonts, selectedFontData, defaultOption]);

  // Since we're using debounced search, filteredFonts is just fontOptions
  const filteredFonts = fontOptions;

  // Calculate virtual items based on scroll position
  const virtualItems = useMemo(() => {
    const totalItems = filteredFonts.length;

    if (totalItems === 0) {
      return { items: [], totalHeight: 0, startIndex: 0, endIndex: 0 };
    }

    const startIndex = Math.floor(scrollTop / ITEM_HEIGHT);
    const endIndex = Math.min(startIndex + VISIBLE_ITEMS, totalItems);

    // Add buffer items
    const bufferStart = Math.max(0, startIndex - BUFFER_SIZE);
    const bufferEnd = Math.min(endIndex + BUFFER_SIZE, totalItems);

    const items = [];
    for (let i = bufferStart; i < bufferEnd; i++) {
      if (filteredFonts[i]) {
        items.push({
          index: i,
          font: filteredFonts[i],
          offsetTop: i * ITEM_HEIGHT,
        });
      }
    }

    return {
      items,
      totalHeight: totalItems * ITEM_HEIGHT,
      startIndex: bufferStart,
      endIndex: bufferEnd,
    };
  }, [filteredFonts, scrollTop]);

  // Preload visible fonts for better performance
  const visibleFontNames = useMemo(() => {
    return virtualItems.items.map(item => item.font.name).filter(name => name !== 'Auto');
  }, [virtualItems.items]);

  useFontPreloader(visibleFontNames, Math.min(visibleFontNames.length, 10));

  // Find current selected font object
  const selectedFont = useMemo(() => {
    return fontOptions.find(font => font._id === value) || defaultOption;
  }, [value, fontOptions, defaultOption]);

  // Handle scroll events for virtualization and infinite loading
  const handleScroll = useCallback(
    event => {
      const newScrollTop = event.target.scrollTop;
      const { scrollHeight, clientHeight } = event.target;

      setScrollTop(newScrollTop);

      // Check if we're near the bottom and should load more
      const threshold = 100; // pixels from bottom
      const distanceFromBottom = scrollHeight - clientHeight - newScrollTop;

      if (distanceFromBottom < threshold && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  const handleChange = fontId => {
    onChange(fontId);
  };

  const handleInputChange = event => {
    setQuery(event.target.value);
    setScrollTop(0); // Reset scroll when searching
  };

  const handleClear = () => {
    setQuery('');
    setScrollTop(0);
    onChange('');
  };

  // Determine the appropriate message to display
  const getNoItemsMessage = () => {
    if (loading) return 'Loading fonts...';
    if (isFetchingNextPage) return 'Loading more fonts...';
    if (error) return `Error loading fonts: ${error}`;
    if (query.trim() && filteredFonts.length === 1) return 'No fonts found';
    if (filteredFonts.length === 1) return 'No fonts available';
    return 'No fonts found';
  };

  return (
    <FontComboboxCustom
      name={name}
      value={selectedFont}
      onChange={handleChange}
      onChangeInput={handleInputChange}
      onClear={handleClear}
      placeholder={placeholder}
      hasItems={filteredFonts.length > 1 && !loading} // Account for default option
      isError={hasError}
      noItemsMsg={getNoItemsMessage()}
      scrollElementRef={scrollElementRef}
      virtualItems={virtualItems}
      onScroll={handleScroll}
      disabled={loading}
      isLoadingMore={isFetchingNextPage}
      defaultLabel={defaultOptionLabel}
    >
      {virtualItems.items.map(item => (
        <div
          key={item.font._id}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: ITEM_HEIGHT,
            transform: `translateY(${item.offsetTop}px)`,
            display: 'flex',
            alignItems: 'center',
            opacity: loading ? 0.6 : 1,
          }}
        >
          <FontOption font={item.font} />
        </div>
      ))}
    </FontComboboxCustom>
  );
};

FontCombobox.propTypes = propTypes;
FontCombobox.defaultProps = defaultProps;

// Memoized version - only re-renders when value or name changes
const FontComboboxMemo = React.memo(FontCombobox, (prev, next) => {
  return (
    prev.name === next.name &&
    prev.value === next.value &&
    prev.hasError === next.hasError &&
    prev.onChange === next.onChange &&
    prev.defaultLabel === next.defaultLabel
  );
});

// Form field wrapper - subscribes to Formik context, passes stable props to memoized inner
export const FontComboboxField = ({
  name,
  label,
  tooltip,
  placeholder,
  showLabel = true,
  ...props
}) => {
  const [field, meta, helpers] = useField(name);

  // Keep onChange stable via ref so the memoized component doesn't re-render
  const setValueRef = useRef(helpers.setValue);
  setValueRef.current = helpers.setValue;

  const handleChange = useCallback(value => setValueRef.current(value), []);

  const hasError = !!(meta.error && meta.touched);

  return (
    <FontComboboxMemo
      name={name}
      label={label}
      tooltip={tooltip}
      placeholder={placeholder}
      showLabel={showLabel}
      value={field.value}
      onChange={handleChange}
      hasError={hasError}
      {...props}
    />
  );
};

export default FontCombobox;
